import { createApp, h, reactive } from 'vue'
import ToastComponent from '@/components/Toast/index.vue'

/**
 * Toast 配置选项
 * @property {string} message 提示信息文本内容
 * @property {'info' | 'success' | 'warning' | 'error'} [type='info'] 提示类型，决定颜色和样式
 * @property {number} [duration=3000] 自动关闭的延迟时间（毫秒）
 */
interface ToastOptions {
  message: string
  type?: 'info' | 'success' | 'warning' | 'error'
  duration?: number
}

/** 首个 toast 距顶部的距离 */
const START_OFFSET = 20
/** toast 之间的间距 */
const GAP = 12

/** 当前存活的 toast 实例，用于计算堆叠偏移 */
const instances: { container: HTMLElement; state: { offset: number } }[] = []

/** 重新计算所有存活 toast 的顶部偏移，实现纵向堆叠 */
function updateOffsets() {
  let top = START_OFFSET
  instances.forEach(({ container, state }) => {
    state.offset = top
    const el = container.firstElementChild as HTMLElement | null
    top += (el?.offsetHeight || 44) + GAP
  })
}

/**
 * 创建并显示 Toast 提示
 * @function createToast
 * @param {ToastOptions} options Toast 配置选项
 * @returns {Object} Vue 应用实例
 */
function createToast(options: ToastOptions) {
  // 创建 DOM 容器元素
  const container = document.createElement('div')
  // 将容器添加到 body 末尾
  document.body.appendChild(container)

  // 偏移量响应式状态，由 updateOffsets 统一维护
  const state = reactive({ offset: START_OFFSET })
  const instance = { container, state }

  // 创建 Vue 应用实例
  const app = createApp({
    render() {
      return h(ToastComponent, {
        // 展开配置选项作为 props 传递给 Toast 组件
        ...options,
        offset: state.offset,
        // 关闭（开始离场动画）时即让后续 toast 上移补位
        onClose: () => {
          const index = instances.indexOf(instance)
          if (index > -1) instances.splice(index, 1)
          updateOffsets()
        },
        // 离场动画结束后卸载实例并清理 DOM
        onDestroy: () => {
          app.unmount()
          if (document.body.contains(container)) {
            document.body.removeChild(container)
          }
        }
      })
    }
  })

  instances.push(instance)
  // 将 Vue 应用挂载到 DOM 容器
  app.mount(container)
  // 挂载后（进场渲染完成）按实际高度重新计算堆叠位置
  requestAnimationFrame(updateOffsets)
  // 返回应用实例供外部引用和控制
  return app
}

/**
 * Toast 工具对象
 * @namespace toast
 * @example
 * // 信息提示（默认3秒）
 * toast.info('这是一条信息');
 *
 * // 成功提示（默认3秒）
 * toast.success('操作成功');
 *
 * // 警告提示（默认3秒）
 * toast.warning('请注意风险');
 *
 * // 错误提示（默认5秒）
 * toast.error('操作失败');
 */
export const toast = {
  /**
   * 信息提示
   * @param {string} message 提示信息内容
   * @param {number} [duration=3000] 显示时长（毫秒）
   */
  info: (message: string, duration = 3000) => createToast({ message, type: 'info', duration }),

  /**
   * 成功提示
   * @param {string} message 成功信息内容
   * @param {number} [duration=3000] 显示时长（毫秒）
   */
  success: (message: string, duration = 3000) => createToast({ message, type: 'success', duration }),

  /**
   * 警告提示
   * @param {string} message 警告信息内容
   * @param {number} [duration=3000] 显示时长（毫秒）
   */
  warning: (message: string, duration = 3000) => createToast({ message, type: 'warning', duration }),

  /**
   * 错误提示
   * @param {string} message 错误信息内容
   * @param {number} [duration=5000] 显示时长（毫秒），错误信息默认显示更久
   */
  error: (message: string, duration = 5000) => createToast({ message, type: 'error', duration })
}

/**
 * 默认导出 Toast 工具对象
 * @default toast
 */
export default toast
